/**
 * Format a UTC ISO string as a full local datetime in the user's stored timezone.
 * Example output: "03 Apr 2026, 12:53 AM"
 *
 * @param {string|Date} isoString
 * @param {string} timezone - IANA tz name from user profile, e.g. "Africa/Accra"
 * @returns {string}
 */
export function formatDateTime(isoString, timezone = 'UTC') {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(isoString));
}

/**
 * Format a UTC ISO string as a date only in the user's stored timezone.
 * Example output: "03 Apr 2026"
 *
 * @param {string|Date} isoString
 * @param {string} timezone - IANA tz name from user profile
 * @returns {string}
 */
export function formatDate(isoString, timezone = 'UTC') {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(isoString));
}

/**
 * Get today's date as a "yyyy-MM-dd" string in the user's stored timezone.
 * Uses the real wall clock but applies the user's profile timezone — not the device timezone.
 *
 * @param {string} timezone - IANA tz name from user profile
 * @returns {string} e.g. "2026-04-03"
 */
export function todayInTimezone(timezone = 'UTC') {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Get a date N days ago as a "yyyy-MM-dd" string in the user's stored timezone.
 *
 * @param {number} daysAgo
 * @param {string} timezone - IANA tz name from user profile
 * @returns {string} e.g. "2026-03-27"
 */
export function daysAgoInTimezone(daysAgo, timezone = 'UTC') {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}
